import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

import * as Yup from "yup";
import { IoClose } from "react-icons/io5";

import { AuthContext } from "../Contexts/AuthContext";
import { Button, Input, Select } from "./";
import { useApi } from "../Helpers/ApiHook";
import CustomAlert from "./CustomAlert";

/*
|--------------------------------------------------------------------------
| Popup Storage Keys
|--------------------------------------------------------------------------
|
| SUBMITTED:
| User has successfully submitted the lead form.
|
| DISMISSED:
| User manually closed the popup.
|
| sessionStorage is deliberately used here so the popup does not annoy
| the user while navigating across different pages in the same browser tab.
|
| Once the tab/browser session ends, the popup becomes eligible to show
| again on a future visit.
|
*/

const LEAD_SUBMITTED_KEY = "fwpi_lead_popup_submitted";
const LEAD_DISMISSED_KEY = "fwpi_lead_popup_dismissed";

/*
|--------------------------------------------------------------------------
| Validation Schema
|--------------------------------------------------------------------------
*/

const leadSchema = Yup.object().shape({
    name: Yup.string()
        .trim()
        .min(2, "Enter a valid name")
        .required("Name is required"),

    mobile: Yup.string()
        .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits")
        .required("Mobile number is required"),

    email: Yup.string()
        .email("Enter a valid email")
        .required("Email ID is required"),

    state_id: Yup.string()
        .required("State is required"),

    city: Yup.string()
        .trim()
        .required("City is required"),
});

/*
|--------------------------------------------------------------------------
| Initial Form Values
|--------------------------------------------------------------------------
*/

const initialValues = {
    name: "",
    mobile: "",
    email: "",
    state_id: "",
    city: "",
};

/*
|--------------------------------------------------------------------------
| Lead Generation Popup
|--------------------------------------------------------------------------
*/

const LeadGenerationPopup = () => {
    const { token } = useContext(AuthContext);
    const { get, post } = useApi();

    const [isOpen, setIsOpen] = useState(false);

    /*
     * Prevent the popup from repeatedly triggering during
     * the current component lifecycle.
     */
    const [hasTriggered, setHasTriggered] = useState(false);

    const [states, setStates] = useState([]);
    const [isLoadingStates, setIsLoadingStates] = useState(false);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const [values, setValues] = useState(initialValues);
    const [errors, setErrors] = useState({});

    /*
    |--------------------------------------------------------------------------
    | Check Whether Popup Should Be Disabled
    |--------------------------------------------------------------------------
    |
    | Do not show popup when:
    |
    | 1. User is logged in
    | 2. Lead was already submitted
    | 3. User already closed the popup
    |
    */

    const shouldSkipPopup = useMemo(() => {
        if (token) {
            return true;
        }

        if (typeof window === "undefined") {
            return false;
        }

        const hasSubmitted =
            sessionStorage.getItem(LEAD_SUBMITTED_KEY) === "true";

        const hasDismissed =
            sessionStorage.getItem(LEAD_DISMISSED_KEY) === "true";

        return hasSubmitted || hasDismissed;
    }, [token]);

    /*
    |--------------------------------------------------------------------------
    | Close Popup
    |--------------------------------------------------------------------------
    |
    | IMPORTANT:
    |
    | Previously setHasTriggered(false) was being called here.
    |
    | That caused the scroll listener to become active again and therefore
    | the popup reopened as soon as the user scrolled.
    |
    | Now:
    |
    | - Popup is marked dismissed
    | - Popup closes
    | - Trigger remains completed
    |
    */

    const handleClose = useCallback(() => {
        if (typeof window !== "undefined") {
            sessionStorage.setItem(LEAD_DISMISSED_KEY, "true");
        }

        setIsOpen(false);
        setHasTriggered(true);
    }, []);

    /*
    |--------------------------------------------------------------------------
    | Trigger Popup on First Scroll
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        /*
         * Do nothing when:
         *
         * - user already submitted
         * - user already dismissed
         * - user is logged in
         * - popup already triggered
         */
        if (shouldSkipPopup || hasTriggered) {
            return;
        }

        const handleFirstScroll = () => {
            /*
             * Double protection in case multiple events
             * fire almost simultaneously.
             */
            setHasTriggered(true);
            setIsOpen(true);

            window.removeEventListener("scroll", handleFirstScroll);
            window.removeEventListener("wheel", handleFirstScroll);
            window.removeEventListener("touchmove", handleFirstScroll);
        };

        window.addEventListener("scroll", handleFirstScroll, {
            passive: true,
        });

        window.addEventListener("wheel", handleFirstScroll, {
            passive: true,
        });

        window.addEventListener("touchmove", handleFirstScroll, {
            passive: true,
        });

        return () => {
            window.removeEventListener("scroll", handleFirstScroll);
            window.removeEventListener("wheel", handleFirstScroll);
            window.removeEventListener("touchmove", handleFirstScroll);
        };
    }, [hasTriggered, shouldSkipPopup]);

    /*
    |--------------------------------------------------------------------------
    | Load Indian States
    |--------------------------------------------------------------------------
    |
    | States are loaded only when the popup actually opens.
    |
    */

    useEffect(() => {
        if (!isOpen || states.length > 0) {
            return;
        }

        const loadIndianStates = async () => {
            setIsLoadingStates(true);

            try {
                const countryResponse = await get("countries/list", {});

                const countries = countryResponse?.data || [];

                const india = countries.find((country) => {
                    const name = String(
                        country?.name || ""
                    ).toLowerCase();

                    const code = String(
                        country?.code || ""
                    ).toLowerCase();

                    return name === "india" || code === "in";
                });

                const stateResponse = await get(
                    `states/${india?.id || 2}`,
                    {}
                );

                setStates(stateResponse?.data || []);
            } catch (error) {
                console.error(
                    "Unable to load Indian states:",
                    error
                );
            } finally {
                setIsLoadingStates(false);
            }
        };

        loadIndianStates();
    }, [get, isOpen, states.length]);

    /*
    |--------------------------------------------------------------------------
    | Popup Keyboard / Body Scroll Handling
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handleEscape = (event) => {
            if (event.key === "Escape") {
                handleClose();
            }
        };

        /*
         * Prevent page scrolling behind the popup.
         */
        const previousOverflow = document.body.style.overflow;

        document.body.style.overflow = "hidden";

        window.addEventListener("keydown", handleEscape);

        return () => {
            document.body.style.overflow = previousOverflow;

            window.removeEventListener(
                "keydown",
                handleEscape
            );
        };
    }, [handleClose, isOpen]);

    /*
    |--------------------------------------------------------------------------
    | Input Change
    |--------------------------------------------------------------------------
    */

    const handleChange = (event) => {
        const { name, value } = event.target;

        /*
         * Mobile number:
         *
         * - Digits only
         * - Maximum 10 digits
         */
        const nextValue =
            name === "mobile"
                ? value.replace(/\D/g, "").slice(0, 10)
                : value;

        setValues((prev) => ({
            ...prev,
            [name]: nextValue,
        }));

        /*
         * Remove validation error as soon as
         * user starts correcting the field.
         */
        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: undefined,
            }));
        }
    };

    /*
    |--------------------------------------------------------------------------
    | Submit Lead
    |--------------------------------------------------------------------------
    */

    const handleSubmit = async (event) => {
        event.preventDefault();

        /*
         * Avoid accidental double submission.
         */
        if (isSubmitting) {
            return;
        }

        setIsSubmitting(true);

        try {
            /*
             * Validate form.
             */
            await leadSchema.validate(values, {
                abortEarly: false,
            });

            setErrors({});

            /*
             * Submit lead.
             */
            const response = await post(
                "/lead/submit",
                values
            );

            /*
             * Successful Submission
             */
            if (response?.status) {
                /*
                 * Remember successful submission for
                 * the complete browser session.
                 */
                if (typeof window !== "undefined") {
                    sessionStorage.setItem(
                        LEAD_SUBMITTED_KEY,
                        "true"
                    );

                    /*
                     * Also mark it dismissed.
                     *
                     * Not strictly necessary because submitted
                     * alone is enough, but this guarantees that
                     * no other route/component lifecycle opens it.
                     */
                    sessionStorage.setItem(
                        LEAD_DISMISSED_KEY,
                        "true"
                    );
                }

                /*
                 * Prevent future trigger.
                 */
                setHasTriggered(true);

                /*
                 * Reset form.
                 */
                setValues(initialValues);

                /*
                 * Close popup.
                 */
                setIsOpen(false);

                /*
                 * Success message.
                 */
                CustomAlert({
                    type: "success",
                    title: "Details Submitted!",
                    message:
                        "Thank you. The FWPI team will contact you soon.",
                    confirmText: "Okay",
                });
            }
        } catch (error) {
            /*
             * Yup validation errors.
             */
            if (error?.name === "ValidationError") {
                const formErrors = {};

                error.inner.forEach((fieldError) => {
                    if (
                        fieldError.path &&
                        !formErrors[fieldError.path]
                    ) {
                        formErrors[fieldError.path] =
                            fieldError.message;
                    }
                });

                setErrors(formErrors);
            } else {
                /*
                 * API or unexpected error.
                 */
                console.error(
                    "Lead submission error:",
                    error
                );
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    /*
    |--------------------------------------------------------------------------
    | Don't Render Popup
    |--------------------------------------------------------------------------
    */

    if (!isOpen || shouldSkipPopup) {
        return null;
    }

    /*
    |--------------------------------------------------------------------------
    | Popup UI
    |--------------------------------------------------------------------------
    */

    return (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/75 px-4 py-5 font-urbanist backdrop-blur-sm">

            <div className="relative w-full max-w-lg max-h-[calc(100vh-2.5rem)] overflow-y-auto rounded-2xl border border-white/15 bg-[#11141C] shadow-2xl">

                {/* Close Button */}

                <button
                    type="button"
                    aria-label="Close lead form"
                    onClick={handleClose}
                    className="absolute right-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white transition hover:bg-white hover:text-black"
                >
                    <IoClose size={24} />
                </button>

                <div>
                    <form
                        className="space-y-4 px-5 py-8 sm:px-7"
                        onSubmit={handleSubmit}
                        noValidate
                    >

                        {/* Heading */}

                        <div className="pr-10">
                            <h3 className="text-2xl font-bold text-white">
                                Get a Callback
                            </h3>

                            <p className="mt-1 text-sm text-gray-400">
                                Fill the details below.
                            </p>
                        </div>

                        {/* Name */}

                        <Input
                            label="Name"
                            name="name"
                            value={values.name}
                            onChange={handleChange}
                            errors={errors}
                            placeholder="Enter your full name"
                            required
                        />

                        {/* Mobile + Email */}

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">

                            <Input
                                label="Mobile Number"
                                name="mobile"
                                type="tel"
                                value={values.mobile}
                                onChange={handleChange}
                                errors={errors}
                                placeholder="10 digit number"
                                required
                            />

                            <Input
                                label="Email ID"
                                name="email"
                                type="email"
                                value={values.email}
                                onChange={handleChange}
                                errors={errors}
                                placeholder="you@example.com"
                                required
                            />

                        </div>

                        {/* State + City */}

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">

                            <Select
                                label="State"
                                name="state_id"
                                value={values.state_id}
                                onChange={handleChange}
                                errors={errors}
                                options={states}
                                schema={{
                                    name: "name",
                                    value: "id",
                                }}
                                placeholder="Select state"
                                loading={isLoadingStates}
                                required
                            />

                            <Input
                                label="City"
                                name="city"
                                value={values.city}
                                onChange={handleChange}
                                errors={errors}
                                placeholder="Enter city"
                                required
                            />

                        </div>

                        {/* Submit */}

                        <Button
                            type="submit"
                            width="w-full"
                            className="rounded-lg"
                            loading={isSubmitting}
                            disabled={
                                isLoadingStates ||
                                isSubmitting
                            }
                        >
                            Submit Details
                        </Button>

                    </form>
                </div>

            </div>
        </div>
    );
};

export default LeadGenerationPopup;